import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';

import slice from './cart_shipping.slice';

import addresses from 'domains/addresses';
import shippingMethods from 'domains/shipping_methods';
import utils from './cart_shipping.utils';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const selectOriginalAddress = (state: LocalState) => state.originalAddress;
const selectSuggestedAddress = (state: LocalState) => state.suggestedAddress;
const selectConfirmedShippingAddress = (state: LocalState) => state.confirmedAddress;
const selectShippingAddressStatus = (state: LocalState) => state.addressStatus;
const selectShippingMethodStatus = (state: LocalState) => state.methodStatus;
const selectShippingAddressValidationStatus = (state: LocalState) => state.validationStatus;
const selectShippingMethodType = (state: LocalState) => state.type;

const selectCartShipping = (state: LocalState) => {
  return state.saved;
};

const selectCartShippingMethodId = (state: LocalState) => {
  return state.saved?.shipping_method_api_id;
};

const selectHasSavedShippingAddress = (state: LocalState) =>
  !!selectConfirmedShippingAddress(state) || !!selectCartShipping(state);

const selectCartShippingAddress = (state: LocalState) => {
  const shipping = selectCartShipping(state);
  if (shipping && utils.cartShippingIsShipping(shipping)) {
    return addresses.utils.format.fromShippingAddress(shipping);
  }
};

const selectCartPickupRecipient = (state: LocalState) => {
  const shipping = selectCartShipping(state);
  if (shipping && utils.cartShippingIsPickup(shipping)) {
    return shipping.recipient;
  }
};

const selectCartShippingMethod = createSelector(
  [compose(selectCartShippingMethodId, localState), shippingMethods.selectors.selectAll],
  (id, methods) => methods.find((sm) => sm.api_id === id)
);

export default {
  validationOriginal: compose(selectOriginalAddress, localState),
  validationSuggested: compose(selectSuggestedAddress, localState),
  validationConfirmed: compose(selectConfirmedShippingAddress, localState),
  addressStatus: compose(selectShippingAddressStatus, localState),
  methodStatus: compose(selectShippingMethodStatus, localState),
  validationStatus: compose(selectShippingAddressValidationStatus, localState),
  type: compose(selectShippingMethodType, localState),
  saved: compose(selectCartShipping, localState),
  savedMethodId: compose(selectCartShippingMethodId, localState),
  savedAddress: compose(selectCartShippingAddress, localState),
  pickupRecipient: compose(selectCartPickupRecipient, localState),
  // Globalized
  savedMethod: selectCartShippingMethod,
  hasSavedAddress: selectHasSavedShippingAddress,
};
